import pino, { type Logger as PinoLogger, type Bindings } from 'pino'

// create the base pino instance
const baseLogger: PinoLogger = pino({
  level: process.env.LOG_LEVEL || 'info',
  base: {
    env: process.env.NODE_ENV || 'development',
  },
})

type SerializableRecord = Record<string, unknown>

function normalizeObject(input: unknown): SerializableRecord | undefined {
  if (input == null) return undefined

  if (input instanceof Error) {
    return { err: input, message: input.message, stack: input.stack }
  }

  if (typeof input === 'object') {
    return input as SerializableRecord
  }

  if (typeof input === 'string') {
    return { detail: input }
  }

  return { detail: String(input) }
}

function normalizeParams(
  first?: unknown,
  second?: unknown,
): {
  obj?: SerializableRecord
  msg?: string
} {
  if (typeof first === 'string') {
    return { msg: first, obj: normalizeObject(second) }
  }
  return {
    obj: normalizeObject(first),
    msg: typeof second === 'string' ? second : undefined,
  }
}

interface AppLogger {
  info: (first?: unknown, second?: unknown) => void
  warn: (first?: unknown, second?: unknown) => void
  error: (first?: unknown, second?: unknown) => void
  debug: (first?: unknown, second?: unknown) => void
  child: (bindings: Bindings) => AppLogger
}

const logger: AppLogger = {
  info(first?: unknown, second?: unknown) {
    const { obj, msg } = normalizeParams(first, second)
    if (obj && msg) baseLogger.info(obj, msg)
    else if (obj) baseLogger.info(obj)
    else if (msg) baseLogger.info(msg)
  },
  warn(first?: unknown, second?: unknown) {
    const { obj, msg } = normalizeParams(first, second)
    if (obj && msg) baseLogger.warn(obj, msg)
    else if (obj) baseLogger.warn(obj)
    else if (msg) baseLogger.warn(msg)
  },
  error(first?: unknown, second?: unknown) {
    const { obj, msg } = normalizeParams(first, second)
    if (obj && msg) baseLogger.error(obj, msg)
    else if (obj) baseLogger.error(obj)
    else if (msg) baseLogger.error(msg)
  },
  debug(first?: unknown, second?: unknown) {
    const { obj, msg } = normalizeParams(first, second)
    if (obj && msg) baseLogger.debug(obj, msg)
    else if (obj) baseLogger.debug(obj)
    else if (msg) baseLogger.debug(msg)
  },
  child(bindings: Bindings): AppLogger {
    const childBase = baseLogger.child(bindings)
    return {
      info(first?: unknown, second?: unknown) {
        const { obj, msg } = normalizeParams(first, second)
        if (obj && msg) childBase.info(obj, msg)
        else if (obj) childBase.info(obj)
        else if (msg) childBase.info(msg)
      },
      warn(first?: unknown, second?: unknown) {
        const { obj, msg } = normalizeParams(first, second)
        if (obj && msg) childBase.warn(obj, msg)
        else if (obj) childBase.warn(obj)
        else if (msg) childBase.warn(msg)
      },
      error(first?: unknown, second?: unknown) {
        const { obj, msg } = normalizeParams(first, second)
        if (obj && msg) childBase.error(obj, msg)
        else if (obj) childBase.error(obj)
        else if (msg) childBase.error(msg)
      },
      debug(first?: unknown, second?: unknown) {
        const { obj, msg } = normalizeParams(first, second)
        if (obj && msg) childBase.debug(obj, msg)
        else if (obj) childBase.debug(obj)
        else if (msg) childBase.debug(msg)
      },
      child(grandBindings: Bindings) {
        return logger.child({ ...bindings, ...grandBindings })
      },
    }
  },
}

export default logger
